import TurndownService from 'turndown';
import DOMPurify from 'dompurify';

// Turndown instance configured for chat-friendly Markdown
const turndown = new TurndownService({
  headingStyle: 'atx',
  codeBlockStyle: 'fenced',
  bulletListMarker: '-',
});

// Add GFM table support
turndown.addRule('tableCell', {
  filter: ['th', 'td'],
  replacement: (content) => ` ${content.trim().replace(/\n/g, ' ')} |`,
});

turndown.addRule('tableRow', {
  filter: 'tr',
  replacement: (content) => `|${content}\n`,
});

turndown.addRule('table', {
  filter: 'table',
  replacement: (_content, node) => {
    const el = node as HTMLTableElement;
    const rows = Array.from(el.rows);
    if (rows.length === 0) return '';

    const lines: string[] = [];
    rows.forEach((row, rowIdx) => {
      const cells = Array.from(row.cells).map(c => ` ${c.textContent?.trim() || ''} `);
      lines.push(`|${cells.join('|')}|`);
      if (rowIdx === 0) {
        lines.push(`|${cells.map(() => '---').join('|')}|`);
      }
    });
    return `\n${lines.join('\n')}\n`;
  },
});

// Keep line breaks
turndown.addRule('lineBreak', {
  filter: 'br',
  replacement: () => '\n',
});

// Allowlisted tags for sanitization
const ALLOWED_TAGS = [
  'table', 'thead', 'tbody', 'tr', 'th', 'td',
  'p', 'br', 'strong', 'b', 'em', 'i', 'u',
  'ul', 'ol', 'li',
  'a', 'pre', 'code',
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  'blockquote', 'hr', 'span', 'div',
];

const ALLOWED_ATTR = ['href', 'target', 'colspan', 'rowspan'];

/**
 * Handle paste event: extract HTML from clipboard, sanitize, convert to Markdown.
 * Returns the Markdown string to insert, or null if no HTML was found (use default paste).
 */
export function handleRichPaste(e: React.ClipboardEvent): string | null {
  const html = e.clipboardData.getData('text/html');
  if (!html) return null;

  // Sanitize the HTML
  const clean = DOMPurify.sanitize(html, {
    ALLOWED_TAGS,
    ALLOWED_ATTR,
    KEEP_CONTENT: true,
  });

  if (!clean.trim()) return null;

  // Convert sanitized HTML to Markdown
  const markdown = turndown.turndown(clean);
  return markdown.trim() || null;
}

/**
 * Convert TipTap editor HTML content to Markdown for sending.
 * Used when extracting content from the rich text editor before sending to backend.
 */
export function htmlToMarkdown(html: string): string {
  if (!html) return '';
  // TipTap wraps empty content in <p></p>
  if (html === '<p></p>' || html === '<p><br></p>') return '';
  const clean = DOMPurify.sanitize(html, { ALLOWED_TAGS, ALLOWED_ATTR, KEEP_CONTENT: true });
  return turndown.turndown(clean).trim();
}

/**
 * Sanitize pasted HTML for TipTap editor insertion.
 * Strips dangerous tags while keeping formatting.
 */
export function sanitizeHtmlForEditor(html: string): string {
  return DOMPurify.sanitize(html, { ALLOWED_TAGS, ALLOWED_ATTR, KEEP_CONTENT: true });
}

/**
 * Copy message as rich text (HTML + plain text) to clipboard.
 * Renders the message bubble's inner HTML so paste into Word/Sheets/email preserves formatting.
 * Falls back to plain text copy if ClipboardItem API is unavailable.
 */
export async function copyRichText(messageBubbleElement: HTMLElement, plainText: string): Promise<void> {
  const html = messageBubbleElement.innerHTML;
  try {
    const htmlBlob = new Blob([html], { type: 'text/html' });
    const textBlob = new Blob([plainText], { type: 'text/plain' });
    await navigator.clipboard.write([
      new ClipboardItem({ 'text/html': htmlBlob, 'text/plain': textBlob }),
    ]);
  } catch {
    // Fallback for browsers without ClipboardItem support
    await navigator.clipboard.writeText(plainText);
  }
}
